import { Link } from "@tanstack/react-router";
import type * as React from "react";
import {
  EntityCardContent,
  EntityCardsGrid,
  entityCardClass,
} from "@/components/shared/entity-cards";
import type { PersonListRow } from "@/hooks/use-persons";
import { carryListSearch } from "@/lib/list-search";
import { PersonAvatar } from "./PersonAvatar";
import { PersonEmploymentTypeBadge } from "./person-badges";

interface PersonCardsProps {
  persons: PersonListRow[];
  loading?: boolean;
  /** Rendered in place of the grid when there are no people (shared with the table view). */
  empty: React.ReactNode;
}

/**
 * Card layout for the person directory: a larger avatar on the left with the
 * person's name and role (their `title`) beside it. Uses the shared entity-card
 * grid/chrome, passing the person avatar as the `leading` slot in place of the
 * generic icon tile. Each card is a real link to the person's detail page.
 */
export function PersonCards({ persons, loading, empty }: PersonCardsProps) {
  return (
    <EntityCardsGrid loading={loading} isEmpty={persons.length === 0} empty={empty}>
      {persons.map((person) => (
        <Link
          key={person.id}
          to="/dashboard/persons/$personId"
          params={{ personId: person.id }}
          search={carryListSearch()}
          aria-label={`Open ${person.name}`}
          className={entityCardClass(true)}
        >
          <EntityCardContent
            leading={
              <PersonAvatar
                name={person.name}
                seed={person.id}
                size="lg"
                imageUrl={person.avatarUrl}
              />
            }
            title={person.name}
            subtitle={person.title || "No role assigned"}
            trailing={<PersonEmploymentTypeBadge employmentType={person.employmentType} />}
          />
        </Link>
      ))}
    </EntityCardsGrid>
  );
}
